import React, {useEffect, useState} from 'react';

import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Button from 'react-bootstrap/Button';
import ListGroup from 'react-bootstrap/ListGroup';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';

import ReactJson from 'react-json-view';

import {TeamTable} from './TeamEditor.js';
import {analysisFields, analysisNameToFieldMap, questionnaireData, toListGroupCompact} from './Client.js';

import {createIsoDate, dateTimeToDE, dateToDE, orderByField} from '../util/Util.js';
import {makeButton} from "../util/Analyses";
import {genders} from "../util/Forms";
import {Info} from "../util/UtilComponents";


/*
Renders a detailed view of a Client, with options for viewing or requesting analyses etc.
 */
let ClientDetails = ({
	products,
	productData,
	createAnalysis,
	selectAnalysis,
	selectKairogramm,
	issueQuestionnaire,
	checkForQuestionnaire,
	client,
	showInfo,
	deleteNewestQuestionnaire,
}) => {
	// small busy logic: count up when starting a request, down when finishing; disable retrieval button while busy
	const [busy, setBusy] = useState();

	// client data
	const clientHeader = `${client.name} ${client.surname} ${client.person ? ` (${genders[client.person.gender].label.substring(0, 1)})` : ''}`;

	const clientData = (
		<ListGroup variant = 'flush'>
			{toListGroupCompact(client)}
		</ListGroup>
	);


	// team clients
	let teamData;

	if (!client.person) {
		teamData = (
			<>
				<Row>
					<Col>
						<TeamTable
							team = {client.team}/>
					</Col>
				</Row>
			</>
		);
	}


	// analyses

	const [showingQuestionnaire, setShowingQuestionnaire] = useState();

	// filter for client-type-appropriate products
	products = products.filter(p => p.teamAnalysis === (client.team !== undefined));
	const allProducts = productData.allProducts.filter(p => p.teamAnalysis === (client.team !== undefined));

	// product configuration: date selection

	// initialize with kairosdate of the last created product
	const [date, setDate] = useState(() =>
			analysisFields.map(f => client[f] || [])
					.reduce((l1, l2) => l1.concat(l2), [])
    				.map(a => a.kairosDate)
    				.sort()
    				.reverse()
    				[0] || createIsoDate());

	const onChangeDate = ev => {
		ev.preventDefault();
		setDate(ev.target.value);
	};

	const onRequestAnalysesForDate = ev => {
		ev.preventDefault();

		/*
		create analyses of all types which have no valid one for the set date product AND
		where the newest corresponding questionnaire is not unanswered
		 */
		(async () => {
			const toRequest = [...products]
				.sort(orderByField('tier'))
				.reverse()
				.filter(prod => shouldRequest(prod, client, date))
				.map(prod => prod.name);

			if (toRequest.length) {
				setBusy({
					done: 0,
					requested: toRequest.length,
				});

				for (const name of toRequest) {
					await createAnalysis(date, name);
					// setBusy(b => b - 1);
					setBusy(b => ({
						...b,
						done: b.done + 1,
					}));
				}

				setBusy();
			}
		}) ();
	};

	// popup

	const dateSelection = (
		<input
			type = 'date'
			value = {date}
			onChange = {onChangeDate}/>
	);

	const dateSelectionOverlay = (
		<Popover>
			<Popover.Content>
				{dateSelection}
			</Popover.Content>
		</Popover>
	);

	// header

	const dateSelectionTrigger = (
		<OverlayTrigger
			trigger = {'click'}
			placement = {'top'}
			rootClose = {true}
			overlay = {dateSelectionOverlay}>
			<Button
				className = {'mx-1'}>
				<i className = {'bi-calendar3'}/>
			</Button>
		</OverlayTrigger>
	);

	/*
	if there's at least one analysis for the date: "aktualisieren"
	otherwise: "erzeugen"
	 */
	const createDocumentsLabel = analysisFields
		.map(f => client[f])
		.find(list => list.find(a => a.kairosDate === date))
		? 'Dokumente aktualisieren' : 'Dokumente erzeugen';

	/*
	allow analysis creation if for the set date at least one product does not have a valid analysis AND
	the newest corresponding questionnaire is not unanswered
	 */
	const disableAnalysisCreation = !products.find(prod => shouldRequest(prod, client, date));

	const headerContent = busy ? (
		<div className = {'d-flex align-items-center'}>
			<div className = {'d-flex align-items-center'}>
				<Spinner className = {'mx-2'}
						 variant = {'dark'}
						 animation = {'border'}/>
				{`${busy.done} von ${busy.requested} fertiggestellt ...`}
			</div>
		</div>
	) : (
		<>
			{dateSelectionTrigger}
			<Button
				className = {'mx-1'}
				disabled = {disableAnalysisCreation}
				title = {'Analysen für Datum anfordern'}
				onClick = {onRequestAnalysesForDate}>
				{createDocumentsLabel}
			</Button>
		</>
	);

	const analysesHeader = (
		<div className = {'d-flex align-items-center'}>
			<div className = {'mb-0 mr-3'}>
				<b>
					{`Analysen für Kairos-Datum ${dateToDE(date)}`}
				</b>
			</div>
			{headerContent}
		</div>
	);


	// products

	// prevent checking for 10s
	const [disableCheck, setDisableCheck] = useState();
	useEffect(() => {
		if (disableCheck) {
			const timer = setTimeout(setDisableCheck, 10000);
			return () => clearTimeout(timer);
		}
	}, [disableCheck]);

	const productParts = allProducts.map(p => {
		let analysis, questionnaireType, onClick, outdated, download, preview, headerText, content;
		// if license is missing
		if (!products
				.map(p => p.name)
				.includes(p.name)) {
			const licenseRequirement = `Benötigt Lizenz '${productData.allLicenses
				.find(l => l.products
					.map(a => a.name)
					.includes(p.name))
				.displayName}'`;

			content = (
				<Row>
					<Col>
						{licenseRequirement}
					</Col>
				</Row>
			);

			headerText = ' ';
		}
		else {
			/*
			select analysis:
			- filter to match the selected date
			- sort by creation date

			then
			- find a valid one if possible
			- otherwise find an invalid one if possible
			 */
			const candidates = client[productFields[p.name]]
				.filter(a => a.kairosDate === date)
				.sort(byCreationDate);

			analysis = candidates.find(a => a.valid);
			if (!analysis && candidates.length)
				analysis = candidates[0];

			outdated = analysis && !analysis.valid;

			// type-dependent UI configuration
			switch (p.name) {
				case 'STANDARD_EINZEL_KAIROSANALYSE':
				case 'PREMIUM_EINZEL_KAIROSANALYSE':
				case 'PERSOENLICHE_KAIROSANALYSE':
				case 'STANDORTBESTIMMUNG':
				case 'TEAM_KAIROSANALYSE': {
					questionnaireType = p.questionnaireType;
					// existing analysis: select; otherwise: request
					onClick = analysis ? selectAnalysis(p.name, analysis.id) : () => createAnalysis(date, p.name);
					download = true;

					break;
				}
				case 'KAIROGRAMM': {
					if (analysis) {
						onClick = () => selectKairogramm(analysis.id);

						if (!outdated) {
							headerText = `Erstellt: ${dateTimeToDE(analysis.creationDate)}`

							const rows = [...analysis.kairogrammTabelle.rows]
								.sort((r1, r2) => r1.values[0] - r2.values[0])
								.map(row => [row.values[2], row.values[3]])
								.map(row => (
									<tr key = {row[0]}>
										<td>
											{row[0]}
										</td>
										<td>
											{row[1]}
										</td>
									</tr>
								));

							preview = (
								<Table className = {'mb-0'}>
									<thead>
										<tr>
											<th key = {'kairogrammTable-preview-Abk'}>
												{'Abk.'}
											</th>
											<th key = {'kairogrammTable-preview-Wert'}>
												{'Wert'}
											</th>
										</tr>
									</thead>
									<tbody>
										{rows}
									</tbody>
								</Table>
							);

						}
						else {
							headerText = '(Veraltet)';
						}
					}

					break;
				}
				case 'KAIROS_KALENDER': {
					headerText = 'Analyse-Dokumente';

					if (analysis && !outdated) {
						headerText = `Erstellt: ${dateTimeToDE(analysis.creationDate)}`

						content = (
							<>
								<Row className = {'mb-2'}>
									<Col className = {'my-auto'}>
										{'Kalender'}
									</Col>
									<Col>
										{makeButton(true, selectAnalysis(p.name, analysis.id, 'CALENDAR'), outdated)}
									</Col>
								</Row>
								<Row className = {'mb-2'}>
									<Col className = {'my-auto'}>
										{'Begleitdokument'}
									</Col>
									<Col>
										{makeButton(true, selectAnalysis(p.name, analysis.id, 'ACCOMPANYING_DOC'), outdated)}
									</Col>
								</Row>
								<Row className = {'mb-2'}>
									<Col className = {'my-auto'}>
										{'ical'}
									</Col>
									<Col>
										{makeButton(true, selectAnalysis(p.name, analysis.id, 'ICAL'), outdated)}
									</Col>
								</Row>
							</>
						);
					}

					break;
				}
			}
		}

		return (
			<ProductPart
				client = {client}
				questionnaireType = {questionnaireType}
				issueQuestionnaire = {issueQuestionnaire}
				setShowingQuestionnaire = {setShowingQuestionnaire}
				checkForQuestionnaire = {checkForQuestionnaire}
				showInfo = {showInfo}
				title = {p.displayName}
				onClick = {onClick}
				analysis = {analysis}
				outdated = {outdated}
				download = {download}
				preview = {preview}
				headerText = {headerText}
				content = {content}
				key = {`product-part-${p.name}`}
				disableCheck = {disableCheck}
				setDisableCheck = {setDisableCheck}
				deleteQuestionnaire = {deleteNewestQuestionnaire}/>
		);
	});



	// render

	return (
		<>
			{/* Klienten-daten */}
			<Card className='mb-4'>
				<Card.Header>
					<b>{clientHeader}</b>
				</Card.Header>
				{clientData}
			</Card>

			{teamData &&
			<Card className='mb-4'>
				<Card.Header>{'Team'}</Card.Header>
				<Card.Body>
					{teamData}
				</Card.Body>
			</Card>}

			{/* analysen */}
			<Card className='mb-3'>
				<Card.Header>
					{analysesHeader}
				</Card.Header>
				<Card.Body>
					<Container>
						<Row>
							{productParts}
						</Row>
					</Container>
				</Card.Body>
			</Card>

			{/* Fragebogen */}
			{showingQuestionnaire &&
			<Modal
				show = {true}
				size = {'lg'}
				onHide = {() => setShowingQuestionnaire(false)}>
				<ReactJson
					src = {JSON.parse(client[showingQuestionnaire].sort(byCreationDate)[0].questionnaire)}
					displayDataTypes = {false}
					displayObjectSize = {false}/>
			</Modal>}
		</>
	);
};

const productFields = {
	STANDARD_EINZEL_KAIROSANALYSE: 'standardEinzelanalysen',
	PREMIUM_EINZEL_KAIROSANALYSE: 'premiumEinzelKairosanalysen',
	PERSOENLICHE_KAIROSANALYSE: 'persoenlicheKairosanalysen',
	STANDORTBESTIMMUNG: 'standortBestimmungen',
	TEAM_KAIROSANALYSE: 'teamKairosanalysen',
	KAIROGRAMM: 'kairogramme',
	KAIROS_KALENDER: 'kairosKalender',
};

// create the UI for each analysis
const ProductPart = ({
	client,
	questionnaireType,
	issueQuestionnaire,
	setShowingQuestionnaire,
	checkForQuestionnaire,
	showInfo,
	title,
	onClick,
	analysis,
	outdated,
	download,
	preview,
	headerText,
	content,
	disableCheck,
	setDisableCheck,
	deleteQuestionnaire,
}) => {
	const questionnairePart = questionnaireType && questionnaireType !== 'None' && (
		<QuestionnairePart
			client = {client}
			questionnaireType = {questionnaireType}
			issueQuestionnaire = {issueQuestionnaire}
			setShowingQuestionnaire = {setShowingQuestionnaire}
			checkForQuestionnaire = {checkForQuestionnaire}
			showInfo = {showInfo}
			disableCheck = {disableCheck}
			setDisableCheck = {setDisableCheck}
			deleteQuestionnaire = {deleteQuestionnaire}/>
	);


	// if content was passed render that; otherwise render a button to trigger retrieval / display

	if (!content) {
		const button = makeButton(download, onClick, !analysis || outdated);

		content = (
			<Row>
				<Col className = {'d-flex justify-content-center'}>
					{button}
				</Col>
			</Row>
		);
	}

	// header
	if (!headerText) {
		headerText = 'Analyse-Dokument';
		if (outdated) {
			headerText += ' (veraltet)';
		}
		else
			if (analysis)
				headerText += ` (erstellt: ${dateTimeToDE(analysis.creationDate)})`;
	}

	const header = outdated ? (
		<Info
			tooltip = {'Seit Erstellung wurde der Klient geändert oder der Fragebogen ausgegeben'}>
			{headerText}
		</Info>
	) : headerText;

	return (
		<Col className = {'col-6 mb-3'}>
			<Card className = {'text-center'}>
				<Card.Header className = {'text-center'}>
					<h5 className={'mb-0'}>{title}</h5>
				</Card.Header>
				<ListGroup variant = {'flush'}>
					{preview &&
						<ListGroup.Item>
							<div>
								{preview}
							</div>
						</ListGroup.Item>}
					{questionnairePart &&
						<ListGroup.Item>
							<div className = {'mb-3'}>
								{questionnairePart}
							</div>
						</ListGroup.Item>}
					<ListGroup.Item>
						<div className = {'mb-3 mt-1'}>
							<h6>{header}</h6>
						</div>
						<Container className = {'mb-3'}>
							{content}
						</Container>
					</ListGroup.Item>
				</ListGroup>
			</Card>
		</Col>
	);
};

const QuestionnairePart = ({
	client,
	questionnaireType,
	issueQuestionnaire,
	setShowingQuestionnaire,
	checkForQuestionnaire,
	showInfo,
	disableCheck,
	setDisableCheck,
	deleteQuestionnaire,
}) => {
	let text, tooltip, label, onClick, disabled;
	const fieldname = questionnaireData[questionnaireType].fieldname;
	const type = questionnaireData[questionnaireType].type;

	const upload = () => issueQuestionnaire(type);

	let questionnaire;
	if (client[fieldname].length)
		questionnaire = client[fieldname].sort(byCreationDate)[0];

	if (questionnaire) {
		text = `Fragebogen (ausgegeben: ${dateTimeToDE(questionnaire.creationDate)})`;

		if (questionnaire.questionnaire) {
			label = 'Anzeigen';
			onClick = () => setShowingQuestionnaire(fieldname);
		}
		else {
			label = 'Abrufen';
			disabled = disableCheck;
			tooltip = 'Prüfen, ob ein ausgefüllter Fragebogen vorliegt';
			onClick = () => (async () => {
				setDisableCheck(true);
				const response = await checkForQuestionnaire(type);
				if (!response) {
					showInfo.show('Noch keine Antwort vorhanden');
				}
			})();
		}
	}
	else {
		text = `Fragebogen`;
		label = 'Ausgeben';
		onClick = upload;
	}

	let deleteQuestionnaireButton;
	if (deleteQuestionnaire) {
		// disable if no questionnaires of the given type exist, or the newest one is answered
		const disableButton = !client[fieldname].length || [...client[fieldname]]
			.sort(byCreationDate)
			.reverse()
			[0].questionnaire;

		const onDeleteQuestionnaire = () => showInfo.show(
			'Warnung: Aktion kann zu Datenverlust führen',
			'Das Zurücksetzen des Fragebogens kann zu Datenverlust führen. Bitte sicherstellen, dass ' +
			'überprüft wurde, ob eine neue Fragebogen-Antwort vorliegt!',
			(
				<>
					<Button className = {'mr-1'}
						variant = {'warning'}
						onClick = {() => {
							deleteQuestionnaire(questionnaireType);
							showInfo.hide();
						}}>
						{'Zurücksetzen'}
					</Button>
					<Button className = {'mr-1'}
							onClick = {showInfo.hide}>
						{'Abbrechen'}
					</Button>
				</>
			)
		);

		deleteQuestionnaireButton = (
			<Button className = {'btn-warning ml-1'}
				disabled = {disableButton}
				onClick = {onDeleteQuestionnaire}>
				{'Zurücksetzen'}
			</Button>
		);
	}

	return (
		<Container>
			<Row className = {'mb-3 justify-content-center'}>
				<Col>
					{text}
				</Col>
			</Row>
			<Row className = {'justify-content-center'}>
				<Col>
					<Button className = {'mr-1'}
						disabled = {disabled}
						title = {tooltip}
						onClick = {onClick}>
						{label}
					</Button>
					{deleteQuestionnaire &&
					deleteQuestionnaireButton
					}
				</Col>
			</Row>
		</Container>
	);
};

export default ClientDetails;

const shouldRequest = (product, client, date) => {
	// request an analysis if no valid analysis for the given date AND newest questionnaire is not unanswered

	// valid analysis? no need to request
	const field = analysisNameToFieldMap[product.name];
	if (client[field].find(a => a.kairosDate === date && a.valid))
		return false;

	// open questionnaire? forbid requesting
	if (product.questionnaireType !== 'None') {
		const questionnaireField = questionnaireData[product.questionnaireType].fieldname;
		const questionnaires = client[questionnaireField].sort(byCreationDate);
		if (questionnaires.length && !questionnaires[0].questionnaire)
			return false;
	}

	return true;
};

const byCreationDate = orderByField('creationDate');
